import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  addMaterialRate,
  getCostingTemplate,
  getMaterialRateAudit,
  listMaterialRates,
  resetCostingTemplate,
  saveCostingTemplate,
  saveMaterialRate,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import { confirmDialog } from "./ui/dialog";
import { useKT } from "./i18n/kt";
import { fmtDay } from "./rates/rateModel";
import { FIELD_LABEL, GROUPS, Group, Kind, KINDS, Layers, Line } from "./costing/costingModel";
import "./costing/karkhana-costing.scss";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

type Rate = { id?: string; name: string; nameHi?: string; unit: string; rate?: string | null; effectiveFrom?: string; active?: boolean; dirty?: boolean };

const Field = ({ label, value, onChange, width, placeholder }: { label: string; value?: string | null; onChange: (v: string) => void; width?: number; placeholder?: string }) => (
  <label className="kc-num" style={width ? { width } : undefined}>
    <span className="kc-num__label">{label}</span>
    <span className="kc-num__box">
      <input value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </span>
  </label>
);

/**
 * The studio's own costing sheet (line names, Hindi names, order, defaults) and its material rates.
 * New costings start from these. Every change is kept on record.
 */
const KarkhanaCostingSetupPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const [lines, setLines] = useState<Line[] | null>(null);
  const [layers, setLayers] = useState<Layers>({});
  const [tplDirty, setTplDirty] = useState(false);
  const [rates, setRates] = useState<Rate[]>([]);
  const [trail, setTrail] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tpl, rs]: any[] = await Promise.all([getCostingTemplate(), listMaterialRates()]);
      if (tpl?.success) {
        setLines(tpl.data.lines || []);
        setLayers(tpl.data.layers || {});
        setTplDirty(false);
      }
      if (rs?.success) setRates((rs.data || []).map((r: any) => ({ ...r })));
    } catch {
      notifications({ title: t("No connection"), message: t("This needs a connection. Try again in a moment."), type: "warning" });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  if (!canManageKarkhana(role)) return null;

  const setLine = (i: number, patch: Partial<Line>) => {
    setLines((ls) => (ls ? ls.map((l, j) => (j === i ? { ...l, ...patch } : l)) : ls));
    setTplDirty(true);
  };
  const move = (i: number, by: number) => {
    setLines((ls) => {
      if (!ls) return ls;
      // Swap with the nearest line of the same group.
      let j = i + by;
      while (j >= 0 && j < ls.length && ls[j].group !== ls[i].group) j += by;
      if (j < 0 || j >= ls.length) return ls;
      const n = [...ls];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
    setTplDirty(true);
  };
  const remove = async (i: number) => {
    const l = lines?.[i];
    if (!l || !(await confirmDialog({ title: t("Remove {{name}} from new costings?", { name: l.label }), body: t("Costings already made keep it."), danger: true, ok: t("Remove") }))) return;
    setLines((ls) => (ls ? ls.filter((_, j) => j !== i) : ls));
    setTplDirty(true);
  };
  const add = (group: Group) => {
    setLines((ls) => [...(ls || []), { group, kind: (group === "MATERIAL" ? "YARN" : "LUMP") as Kind, label: t("New line"), koriFactor: group === "MATERIAL" ? "1" : null }]);
    setTplDirty(true);
  };

  const saveTemplate = async () => {
    if (!lines) return;
    if (lines.some((l) => !l.label.trim())) {
      notifications({ title: t("Not saved"), message: t("Every line needs a name"), type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const res: any = await saveCostingTemplate({ lines, layers });
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      setLines(res.data.lines);
      setLayers(res.data.layers);
      setTplDirty(false);
      setTrail(null);
      notifications({ title: t("Saved"), message: t("New costings will use these lines."), type: "success" });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!(await confirmDialog({ title: t("Go back to the starting line list?"), body: t("Your renamed and added lines are replaced. Costings already made don't change."), danger: true }))) return;
    const res: any = await resetCostingTemplate();
    if (res?.success) {
      setLines(res.data.lines);
      setLayers(res.data.layers);
      setTplDirty(false);
      setTrail(null);
    }
  };

  const setRate = (i: number, patch: Partial<Rate>) => setRates((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch, dirty: true } : r)));

  const saveRate = async (i: number) => {
    const r = rates[i];
    if (!r.name.trim()) {
      notifications({ title: t("Not saved"), message: t("Enter the material name"), type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const body = { name: r.name, nameHi: r.nameHi || "", unit: r.unit, rate: r.rate ?? "", active: r.active !== false };
      const res: any = r.id ? await saveMaterialRate(r.id, body) : await addMaterialRate(body);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      setRates((rs) => rs.map((x, j) => (j === i ? { ...res.data } : x)));
      setTrail(null);
      notifications({ title: t("Saved"), message: t("New costings will use this rate. The old rate stays on record."), type: "success" });
    } finally {
      setBusy(false);
    }
  };

  const openTrail = async () => {
    if (trail) return;
    try {
      const res: any = await getMaterialRateAudit();
      setTrail(res?.success ? res.data || [] : []);
    } catch {
      setTrail([]);
    }
  };

  return (
    <div className="gi-registry-page kw-page kc-page">
      <PageHeader title={t("Costing lines and material rates")} back={kp.costings()} crumbs={[{ to: kp.costings(), label: t("Costing") }]} trail={t("Lines and rates")}>
        <HeaderButton onClick={saveTemplate} disabled={busy || !tplDirty} primary={tplDirty}>
          {t("Save lines")}
        </HeaderButton>
      </PageHeader>

      <section className="kc-card">
        <h3 className="kc-group__title">
          <span>
            {t("Material rates")} <small>सामान के दाम</small>
          </span>
        </h3>
        <p className="kc-muted">{t("Today's price you pay. New costings take the rate from here; each costing keeps the rate it used.")}</p>
        <div className="kc-table">
          {rates.map((r, i) => (
            <div key={r.id || `new-${i}`} className="kc-rate-row">
              <Field label={t("Material")} value={r.name} onChange={(v) => setRate(i, { name: v })} />
              <Field label={t("Hindi name")} value={r.nameHi} onChange={(v) => setRate(i, { nameHi: v })} />
              <label className="kc-num">
                <span className="kc-num__label">{t("Per")}</span>
                <span className="kc-num__box">
                  <select value={r.unit} onChange={(e) => setRate(i, { unit: e.target.value })}>
                    <option value="KG">kg</option>
                    <option value="G">g</option>
                    <option value="METRE">metre</option>
                    <option value="PIECE">piece</option>
                  </select>
                </span>
              </label>
              <Field label={t("Rate ₹")} value={r.rate} onChange={(v) => setRate(i, { rate: v })} placeholder={t("not set")} />
              <span className="kc-from">
                {r.effectiveFrom ? t("since {{d}}", { d: fmtDay(r.effectiveFrom) }) : ""}
                {r.active === false ? ` · ${t("hidden")}` : ""}
              </span>
              <span style={{ display: "flex", gap: 6, paddingBottom: 2 }}>
                {r.id ? (
                  <button type="button" className="kh-btn kh-btn--sm" onClick={() => setRate(i, { active: r.active === false })}>
                    {r.active === false ? t("Show") : t("Hide")}
                  </button>
                ) : null}
                <button type="button" className="kh-btn kh-btn--sm is-primary" onClick={() => saveRate(i)} disabled={busy || !r.dirty}>
                  {t("Save")}
                </button>
              </span>
            </div>
          ))}
        </div>
        <button type="button" className="kc-addlink" onClick={() => setRates((rs) => [...rs, { name: "", unit: "KG", dirty: true }])}>
          + {t("Add a material")}
        </button>
        <details onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && openTrail()} style={{ marginTop: 8 }}>
          <summary className="kc-muted">{t("Rate and line history")}</summary>
          <ol className="kc-audit">
            {(trail || []).map((a: any) => (
              <li key={a.id}>
                <div>
                  <strong>{a.action.replace(/_/g, " ").toLowerCase()}</strong> · {a.byName} ·{" "}
                  {new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </div>
                <ul>
                  {(a.changes || []).slice(0, 30).map((ch: any, k: number) => (
                    <li key={k}>
                      {ch.label} · {FIELD_LABEL[ch.field] || ch.field}: {ch.from ?? "—"} → {ch.to ?? "—"}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {trail && !trail.length ? <li>{t("Nothing yet.")}</li> : null}
          </ol>
        </details>
      </section>

      <section className="kc-card">
        <h3 className="kc-group__title">
          <span>
            {t("Starting %s for new costings")} <small>नई लागत के लिए</small>
          </span>
        </h3>
        <div className="kc-inputs">
          <Field label={t("Business overhead %")} value={layers.overheadPct} onChange={(v) => { setLayers({ ...layers, overheadPct: v }); setTplDirty(true); }} width={150} />
          <Field label={t("Profit %")} value={layers.profitPct} onChange={(v) => { setLayers({ ...layers, profitPct: v }); setTplDirty(true); }} width={110} />
          <Field label={t("Discount %")} value={layers.discountPct} onChange={(v) => { setLayers({ ...layers, discountPct: v }); setTplDirty(true); }} width={110} />
          <Field label={t("GST %")} value={layers.gstPct} onChange={(v) => { setLayers({ ...layers, gstPct: v }); setTplDirty(true); }} width={90} />
          <Field label={t("Round sale price up to ₹")} value={layers.roundTo} onChange={(v) => { setLayers({ ...layers, roundTo: v }); setTplDirty(true); }} width={170} />
        </div>
      </section>

      {!lines ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {lines
        ? GROUPS.map((g) => {
            const rows = lines.map((l, i) => ({ l, i })).filter((x) => x.l.group === g.id);
            return (
              <section key={g.id} className="kc-group">
                <h3 className="kc-group__title">
                  <span>
                    {t(g.title)} <small>{g.hi}</small>
                  </span>
                </h3>
                <div className="kc-table">
                  {rows.map(({ l, i }) => (
                    <div key={l.id || `n-${i}`} className="kc-setup-row">
                      <Field label={t("Line name")} value={l.label} onChange={(v) => setLine(i, { label: v })} />
                      <Field label={t("Hindi name")} value={l.labelHi} onChange={(v) => setLine(i, { labelHi: v })} />
                      <label className="kc-num">
                        <span className="kc-num__label">{t("How it's worked out")}</span>
                        <span className="kc-num__box">
                          <select value={l.kind} onChange={(e) => setLine(i, { kind: e.target.value as Kind })}>
                            {KINDS.map((k) => (
                              <option key={k.id} value={k.id}>
                                {t(k.title)}
                              </option>
                            ))}
                          </select>
                        </span>
                      </label>
                      <label className="kc-num">
                        <span className="kc-num__label">{t("Group")}</span>
                        <span className="kc-num__box">
                          <select value={l.group} onChange={(e) => setLine(i, { group: e.target.value as Group })}>
                            {GROUPS.map((x) => (
                              <option key={x.id} value={x.id}>
                                {t(x.title)}
                              </option>
                            ))}
                          </select>
                        </span>
                      </label>
                      {l.kind === "YARN" ? (
                        <Field label={t("Kori factor")} value={l.koriFactor} onChange={(v) => setLine(i, { koriFactor: v })} />
                      ) : l.kind === "LABOUR" ? (
                        <Field label={t("₹ per day")} value={l.dayRate} onChange={(v) => setLine(i, { dayRate: v })} />
                      ) : (
                        <span />
                      )}
                      <span className="kc-move">
                        <button type="button" aria-label={t("Move up")} onClick={() => move(i, -1)}>
                          ↑
                        </button>
                        <button type="button" aria-label={t("Move down")} onClick={() => move(i, 1)}>
                          ↓
                        </button>
                        <button type="button" aria-label={t("Remove {{name}}", { name: l.label })} onClick={() => remove(i)}>
                          ×
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
                <button type="button" className="kc-addlink" onClick={() => add(g.id)}>
                  + {t("Add a line")}
                </button>
              </section>
            );
          })
        : null}
      {lines ? (
        <button type="button" className="kc-discard" onClick={reset}>
          {t("Go back to the starting line list")}
        </button>
      ) : null}
    </div>
  );
};

export default KarkhanaCostingSetupPage;
